import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { FiAlertTriangle, FiHelpCircle } from 'react-icons/fi';

interface ConfirmOptions {
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'primary' | 'danger' | 'success';
  closeOnOverlayClick?: boolean;
}

interface PromptOptions extends ConfirmOptions {
  placeholder?: string;
  initialValue?: string;
  validate?: ((value: string) => string | null) | null;
}

interface DialogState {
  type: 'confirm' | 'prompt';
  options: ConfirmOptions & PromptOptions;
  resolve: (result: boolean | string | null) => void;
}

export interface DialogContextValue {
  confirm: (options?: ConfirmOptions) => Promise<boolean>;
  prompt: (options?: PromptOptions) => Promise<string | null>;
}

const DEFAULT_CONFIRM_OPTIONS: Required<ConfirmOptions> = {
  title: 'Confirmar acción',
  message: '',
  confirmText: 'Confirmar',
  cancelText: 'Cancelar',
  variant: 'primary',
  closeOnOverlayClick: true,
};

const DEFAULT_PROMPT_OPTIONS: Required<PromptOptions> = {
  ...DEFAULT_CONFIRM_OPTIONS,
  title: 'Ingresar valor',
  confirmText: 'Guardar',
  placeholder: '',
  initialValue: '',
  validate: null,
};

const getConfirmClass = (variant?: string) => {
  if (variant === 'danger') return 'btn-danger';
  if (variant === 'success') return 'btn-success';
  return 'btn-primary';
};

interface DialogRendererProps {
  dialog: DialogState | null;
  onCancel: () => void;
  onConfirm: (result: boolean | string) => void;
}

const DialogRenderer: React.FC<DialogRendererProps> = ({ dialog, onCancel, onConfirm }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState(dialog?.type === 'prompt' ? dialog.options.initialValue || '' : '');
  const [validationError, setValidationError] = useState('');

  useEffect(() => {
    if (dialog?.type === 'prompt') {
      setValue(dialog.options.initialValue || '');
      setValidationError('');
      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 0);
    }
  }, [dialog]);

  if (!dialog) return null;

  const handleCancel = () => onCancel();

  const handleConfirm = () => {
    if (dialog.type === 'prompt' && typeof dialog.options.validate === 'function') {
      const errorMessage = dialog.options.validate(value);
      if (errorMessage) {
        setValidationError(errorMessage);
        return;
      }
    }
    onConfirm(dialog.type === 'prompt' ? value : true);
  };

  const handleOverlayClick = () => {
    if (dialog.options.closeOnOverlayClick) handleCancel();
  };

  const icon = dialog.options.variant === 'danger'
    ? <FiAlertTriangle className="w-6 h-6 text-danger-600" />
    : <FiHelpCircle className="w-6 h-6 text-primary-600" />;

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-content max-w-lg" role="dialog" aria-modal="true" aria-labelledby="dialog-title" onClick={e => e.stopPropagation()}>
        <div className="card-header flex items-start gap-3">
          <div className="mt-0.5">{icon}</div>
          <div>
            <h3 id="dialog-title" className="text-lg font-semibold text-content-primary">{dialog.options.title}</h3>
            {dialog.options.message && <p className="text-sm text-content-secondary mt-1">{dialog.options.message}</p>}
          </div>
        </div>
        <div className="card-body">
          {dialog.type === 'prompt' && (
            <div>
              <input
                ref={inputRef}
                type="text"
                value={value}
                onChange={e => {
                  setValue(e.target.value);
                  if (validationError) setValidationError('');
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleConfirm();
                  }
                }}
                className="input-base"
                placeholder={dialog.options.placeholder}
              />
              {validationError && <p className="text-sm text-danger-600 mt-2">{validationError}</p>}
            </div>
          )}
          <div className="flex justify-end gap-2 mt-4">
            <button type="button" onClick={handleCancel} className="btn-secondary">{dialog.options.cancelText}</button>
            <button type="button" onClick={handleConfirm} className={getConfirmClass(dialog.options.variant)}>{dialog.options.confirmText}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

const DialogContext = createContext<DialogContextValue | null>(null);

interface DialogProviderProps {
  children: React.ReactNode;
}

export const DialogProvider: React.FC<DialogProviderProps> = ({ children }) => {
  const [dialog, setDialog] = useState<DialogState | null>(null);

  useEffect(() => {
    if (!dialog) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        dialog.resolve(dialog.type === 'prompt' ? null : false);
        setDialog(null);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [dialog]);

  const confirm = useCallback((options?: ConfirmOptions): Promise<boolean> => {
    const mergedOptions = { ...DEFAULT_CONFIRM_OPTIONS, ...(options || {}) };
    return new Promise(resolve => {
      setDialog({ type: 'confirm', options: mergedOptions, resolve: resolve as (result: boolean | string | null) => void });
    });
  }, []);

  const prompt = useCallback((options?: PromptOptions): Promise<string | null> => {
    const mergedOptions = { ...DEFAULT_PROMPT_OPTIONS, ...(options || {}) };
    return new Promise(resolve => {
      setDialog({ type: 'prompt', options: mergedOptions, resolve: resolve as (result: boolean | string | null) => void });
    });
  }, []);

  const value = useMemo(() => ({ confirm, prompt }), [confirm, prompt]);

  const handleCancel = useCallback(() => {
    if (!dialog) return;
    dialog.resolve(dialog.type === 'prompt' ? null : false);
    setDialog(null);
  }, [dialog]);

  const handleConfirm = useCallback((result: boolean | string) => {
    if (!dialog) return;
    dialog.resolve(result);
    setDialog(null);
  }, [dialog]);

  return (
    <DialogContext.Provider value={value}>
      {children}
      <DialogRenderer dialog={dialog} onCancel={handleCancel} onConfirm={handleConfirm} />
    </DialogContext.Provider>
  );
};

export const useDialog = (): DialogContextValue => {
  const context = useContext(DialogContext);
  if (!context) {
    throw new Error('useDialog must be used within a DialogProvider');
  }
  return context;
};

export const useConfirm = () => {
  const { confirm } = useDialog();
  return confirm;
};

export const usePrompt = () => {
  const { prompt } = useDialog();
  return prompt;
};
